import React from "react";
import { Warning, Target, Users, TrendUp } from "@phosphor-icons/react";

/**
 * Slate-adaptive banner shown at the top of the Optimizer.
 * - MAIN: shows nothing (all defaults active)
 * - TURBO/MINI: informs user that stack matrix has been auto-adjusted
 * - SHOWDOWN: shows the Captain + 5 FLEX ruleset instead of Combat Stack Matrix
 */
export default function SlateModeBanner({ slateType, slateMeta, contestType, presetKey }) {
  if (!slateType || slateType === "MAIN") return null;
  // Sprint 60.72 — Suppress the MINI / TURBO banner when the user has
  // Milly Maker picked (via either the top-level contestType picker or
  // the preset key from Optimizer.jsx). Milly is only offered on
  // ≥12-game main slates, so seeing "MINI Slate Detected" here would
  // be contradictory noise. Accepts either signal so both callers work.
  const isMilly = contestType === "MILLY" || presetKey === "milly_maker";
  if (isMilly && (slateType === "MINI" || slateType === "TURBO")) return null;

  const config = {
    TURBO: {
      color: "border-[#FFCC00] bg-[#FFCC00]/5",
      icon: Warning,
      iconColor: "text-[#FFCC00]",
      title: "TURBO Slate Detected",
      subtitle: `${slateMeta?.n_games || "?"}-game slate — stack matrix auto-reduced`,
      rules: [
        "Stack shapes reduced — Primary and Primary+Secondary only",
        "Tertiary game stacks (P_S_T) disabled — insufficient game diversity",
        "Max players per team increased to 5 (larger share of small pool)",
        "Unique Seed + Peer Swap still active",
      ],
    },
    MINI: {
      color: "border-[#FFCC00] bg-[#FFCC00]/5",
      icon: Warning,
      iconColor: "text-[#FFCC00]",
      title: "MINI Slate Detected",
      subtitle: `${slateMeta?.n_games || 2}-game slate — Primary + Secondary correlation forced`,
      rules: [
        "Stack shapes forced to heavy Primary + Secondary",
        "Primary + Secondary game correlation is the whole slate",
        "Higher ownership tolerance — chalk is unavoidable at this scale",
        "Consider single-entry contests to maximize edge",
      ],
    },
    SHOWDOWN: {
      color: "border-[#FF3B30] bg-[#FF3B30]/5",
      icon: Target,
      iconColor: "text-[#FF3B30]",
      title: "SHOWDOWN Slate Detected",
      subtitle: "Single game — Captain + 5 FLEX ruleset active",
      rules: [
        "Roster: 1 CAPTAIN (1.5× salary, 1.5× projection) + 5 FLEX (any position)",
        "Captain restricted to QB / RB / WR / TE (never DST)",
        "Max 5 from one team — both sides of the game must be represented",
        "QB required in every lineup (Captain or FLEX)",
        "Stack rules disabled — game correlation is automatic in a single game",
      ],
    },
  }[slateType];

  if (!config) return null;
  const Icon = config.icon;

  return (
    <div data-testid="slate-mode-banner" className={`border ${config.color} p-4 mb-4`}>
      <div className="flex items-start gap-3">
        <Icon size={24} weight="fill" className={`${config.iconColor} flex-shrink-0 mt-0.5`} />
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="heading font-bold text-sm uppercase tracking-wider">{config.title}</div>
            <span className="text-[10px] mono text-zinc-400">{config.subtitle}</span>
          </div>
          <ul className="mt-2 space-y-1 text-xs text-zinc-400">
            {config.rules.map((r, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-[#39FF14] mt-0.5">→</span>
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

/**
 * Captain slot indicator for Showdown lineups.
 * Renders as a red badge next to the CAPTAIN row.
 */
export function CaptainBadge() {
  return (
    <span className="text-[10px] mono px-2 py-0.5 border border-[#FF3B30] text-[#FF3B30] font-bold tracking-widest">
      CPT · 1.5×
    </span>
  );
}
